import React, { useEffect, useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';

const getApiBaseUrl = () => {
  if (typeof window !== 'undefined' && window.location) {
    const hostname = window.location.hostname;
    const protocol = window.location.protocol;
    if (hostname === 'localhost' || hostname === '127.0.0.1') {
      return `${protocol}//${hostname}:8001/api/v1`;
    }
    return '/api/v1';
  }
  return 'http://localhost:8001/api/v1';
};

const EmailVerifyPage = ({ appLabel = 'Agentic', sourceApp = null }) => {
  const [searchParams] = useSearchParams();
  const token = searchParams.get('token');
  const appParam = searchParams.get('app');
  const emailParam = searchParams.get('email');
  const [status, setStatus] = useState(token ? 'loading' : 'waiting');
  const [message, setMessage] = useState('');
  const [loginUrl, setLoginUrl] = useState('/login');
  const [resending, setResending] = useState(false);
  const [resendMessage, setResendMessage] = useState('');

  useEffect(() => {
    if (!token) {
      return undefined;
    }

    let cancelled = false;
    (async () => {
      try {
        const qs = new URLSearchParams({ token });
        if (appParam) {
          qs.set('app', appParam);
        }
        const contextRes = await fetch(`${getApiBaseUrl()}/auth/verify/context?${qs}`);
        if (contextRes.ok && !cancelled) {
          const ctx = await contextRes.json();
          if (ctx.login_url) {
            setLoginUrl(ctx.login_url);
          }
        }

        const verifyRes = await fetch(`${getApiBaseUrl()}/auth/verify`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ token }),
        });
        if (cancelled) {
          return;
        }
        if (verifyRes.ok) {
          setStatus('success');
          setMessage('Your email has been verified. You can now sign in.');
        } else {
          const err = await verifyRes.json().catch(() => ({}));
          setStatus('error');
          setMessage(err.detail || 'Verification failed. The link may be invalid or expired.');
        }
      } catch {
        if (!cancelled) {
          setStatus('error');
          setMessage('Something went wrong. Please try again.');
        }
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [token, appParam]);

  const goToLogin = () => {
    if (loginUrl.startsWith('http')) {
      window.location.href = loginUrl;
      return;
    }
    window.location.href = loginUrl;
  };

  const handleResend = async () => {
    if (!emailParam) {
      return;
    }
    setResending(true);
    setResendMessage('');
    try {
      const response = await fetch(`${getApiBaseUrl()}/auth/resend-verification-email`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          email: emailParam,
          ...(sourceApp || appParam ? { source_app: sourceApp || appParam } : {}),
        }),
      });
      const data = await response.json().catch(() => ({}));
      if (!response.ok) {
        throw new Error(data.detail || 'Could not resend verification email.');
      }
      setResendMessage(data.message || 'Verification email sent. Check your inbox.');
    } catch (error) {
      setResendMessage(error.message || 'Could not resend verification email.');
    } finally {
      setResending(false);
    }
  };

  return (
    <div className="min-h-screen bg-gray-50 flex items-center justify-center px-4">
      <div className="max-w-md w-full bg-white rounded-lg shadow p-6 text-center">
        <h1 className="text-xl font-bold text-gray-900 mb-2">{appLabel}</h1>
        {status === 'loading' && <p className="text-sm text-gray-600">Verifying your email...</p>}
        {status === 'success' && (
          <>
            <p className="text-sm text-green-700 mb-4">{message}</p>
            <button
              type="button"
              onClick={goToLogin}
              className="w-full py-2 px-4 bg-blue-600 text-white rounded-md hover:bg-blue-700"
            >
              Go to sign in
            </button>
          </>
        )}
        {status === 'error' && (
          <>
            <p className="text-sm text-red-600 mb-4">{message}</p>
            <button
              type="button"
              onClick={goToLogin}
              className="w-full py-2 px-4 border border-gray-300 rounded-md hover:bg-gray-50"
            >
              Back to sign in
            </button>
          </>
        )}
        {status === 'waiting' && (
          <div className="space-y-4">
            <p className="text-sm text-gray-600">
              {emailParam ? (
                <>
                  We sent a verification link to <strong>{emailParam}</strong>. Open it to activate your
                  account, then sign in.
                </>
              ) : (
                <>
                  Check your email for a verification link, or{' '}
                  <Link to="/login" className="text-blue-600 hover:text-blue-700">sign in</Link>.
                </>
              )}
            </p>
            {emailParam && (
              <button
                type="button"
                onClick={handleResend}
                disabled={resending}
                className="w-full py-2 px-4 bg-blue-600 text-white rounded-md hover:bg-blue-700 disabled:opacity-50"
              >
                {resending ? 'Sending...' : 'Resend verification email'}
              </button>
            )}
            {resendMessage && <p className="text-sm text-gray-600">{resendMessage}</p>}
            <button
              type="button"
              onClick={goToLogin}
              className="w-full py-2 px-4 border border-gray-300 rounded-md hover:bg-gray-50"
            >
              Back to sign in
            </button>
          </div>
        )}
      </div>
    </div>
  );
};

export default EmailVerifyPage;
